import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { faSliders } from '@fortawesome/free-solid-svg-icons';
import { PLUGIN_MODULE_PERMISSIONS } from '../shared/permissions.constant';
import {
  IAppDashboardMenu,
  IMenuItem,
  IMenuItemPermissions,
  PluginCommunicationService,
  PluginInjectorApplicationPlugin,
  PluginInjectorService,
} from 'core-client/api';
import { CorePluginActions } from '../shared/core-plugin-communication';
import type { UserPreferencesComponent } from './user-preferences.component';

@Injectable({ providedIn: 'root' })
export class UserPreferencesMenuService {
  private readonly pluginInjectorService = inject(PluginInjectorService);
  private readonly pluginCommunicationService = inject(PluginCommunicationService);
  private readonly dialog = inject(MatDialog);

  private navbarMenuItem!: IMenuItem;
  private readonly pluginModule = 'USER_PREFERENCES';
  private readonly navbarMenuTitle = 'USER_PREFERENCES';
  private readonly menuItemPermissions: IMenuItemPermissions = {
    user_preferences: JSON.parse(JSON.stringify(PLUGIN_MODULE_PERMISSIONS.user_preferences)),
  };

  /** Plugin id captured when the navbar menu item is built. */
  private pluginId: string | null = null;

  /** Tracks only *this* dialog's own ref, so unrelated open dialogs never block us. */
  private openRef: MatDialogRef<UserPreferencesComponent> | null = null;

  /**
   * Set synchronously before the dynamic import, so two callers racing on a
   * cold chunk fetch (e.g. a navbar double-click, or a navbar click plus the
   * quick-select "Audio settings..." action) cannot both pass the `openRef`
   * guard before either has assigned it.
   */
  private opening = false;

  constructor() {
    this.pluginCommunicationService
      .onAction(CorePluginActions.UserPreferences.OPEN, 'core_plugin')
      .subscribe(payload => this.openDialog(payload?.tabId, payload?.sectionParams));
  }

  getNavbarMenuItem(
    pluginConfig: PluginInjectorApplicationPlugin.IApplicationPlugin,
    dashboardMenu: IMenuItem[],
    primaryEnabled: boolean,
    secondaryEnabled: boolean,
  ): IMenuItem[] {
    const menuItemsPermissionsRefs = Object.keys(this.menuItemPermissions['user_preferences']);

    this.pluginId = pluginConfig._id;

    this.navbarMenuItem = {
      pluginId: pluginConfig._id,
      pluginName: pluginConfig.name,
      pluginModule: this.pluginModule,
      nameIdentifier: this.navbarMenuTitle,
      accessRights: menuItemsPermissionsRefs,
      primary: { enabled: primaryEnabled, position: dashboardMenu.length + 1 },
      secondary: { enabled: secondaryEnabled, position: dashboardMenu.length + 1 },
      strictMode: true,
      headerSortOrder: 40,
      icon: faSliders,
      dropdown: false,
      useExternalLink: false,
      disabled: new BehaviorSubject(false),
      callback: async () => {
        await this.openDialog();
      },
    };

    if (!this.doesMenuExists(this.navbarMenuTitle, dashboardMenu)) {
      dashboardMenu.push(this.navbarMenuItem);
    }
    return dashboardMenu;
  }

  getMenuItemPermissions(): IMenuItemPermissions {
    return this.menuItemPermissions;
  }

  private doesMenuExists(title: string, menu: IAppDashboardMenu[]): boolean {
    return menu.some(item => item.nameIdentifier.toLowerCase() === title.toLowerCase());
  }

  /**
   * Opens the user-preferences dialog, optionally preselecting a tab.
   * Guarded against its own already-open instance only — an unrelated
   * dialog being open elsewhere must not block this one.
   */
  private async openDialog(
    tabId?: string,
    sectionParams?: Readonly<Record<string, unknown>>,
  ): Promise<void> {
    if (this.openRef || this.opening) {
      return;
    }
    this.opening = true;
    try {
      const { UserPreferencesComponent } = await import('./user-preferences.component');
      const dialogConfig = this.pluginInjectorService.getMenuDialogConfig();
      this.openRef = this.dialog.open(UserPreferencesComponent, {
        ...dialogConfig,
        data: {
          pluginId: this.pluginId ?? '',
          pluginModule: this.pluginModule,
          initialTabId: tabId,
          sectionParams,
        },
      });
      this.openRef.afterClosed().subscribe(() => {
        this.openRef = null;
      });
    } finally {
      // Cleared here (not just on success) so a rejected dynamic import can
      // never leave the guard stuck permanently true.
      this.opening = false;
    }
  }
}
